// 📁 src/pages/Menus.jsx
// chargement des menus du groupe et affichage des menus via MenusJournalier.
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Box, Typography, useMediaQuery, Paper, Button } from "@mui/material";

import { getPeriodeInfo } from "../utils/periodeUtils.js";
import { logDev } from "../utils/logger.js";
import { calculateTotal } from "../utils/calculateTotal.js";
import { useContactsPeriodeDominante } from "../hooks/useContactsPeriodeDominante.js";

import { useGroupe } from "../context/GroupeContext.js";
import { useLanguage } from "../context/LanguageContext.js";

import ListeMenusJournalier from "../components/MenusJournalier/ListeMenusJournalier.js";
import MenusJournalier from "../components/MenusJournalier/MenusJournalier.js";
import BlocPersonnalise from "../components/MenusJournalier/BlocPersonnalise.js";
import QuotaInfo from "../components/QuotaInfo.js";

import { useCustomBlocks } from "../hooks/useCustomBlocks.js";
import { useAdminRole } from "../hooks/useAdminRole.js";


const Menus = () => {
  const { t } = useLanguage();
  const { groupeId, nomGroupeComplet, couleur, groupeActif } = useGroupe();
  const [contacts, setContacts] = useState([]);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [periodes, setPeriodes] = useState([]);
  const isMobile = useMediaQuery("(max-width:600px)");
  const role = useAdminRole(groupeId);
  const isAdmin = role === "admin";
  const [periodePrivilegiee, setPeriodePrivilegiee] = useState(null);

  const periodeInfo = getPeriodeInfo(contacts, periodes, "fr-FR", periodePrivilegiee);

  logDev("[Menus.jsx] periodeInfo calculé", { 
    contactsCount: contacts.length,
    periodesCount: periodes.length,
    periodeInfo
  });

  const { totalCombien } = useContactsPeriodeDominante(contacts, {
  winningKey: periodeInfo?.dominantKey,
  fallbackAll: false
});
  
  const [menus, setMenus] = useState([]);
  const navigate = useNavigate();
  const [footerHeight, setFooterHeight] = useState(0);
  const [bottomNavHeight, setBottomNavHeight] = useState(0);
  const topOffset = headerHeight + (isMobile ? footerHeight : bottomNavHeight) + 16;
  
  const {
    customBlocks,
    newBlockName,
    setNewBlockName,
    addCustomBlock,
    handleCustomItemChange,
    addCustomItem,
    removeCustomItem,
    renameCustomBlock,
  } = useCustomBlocks(groupeId);

  useEffect(() => {
    const header = document.querySelector('.header');
    const nav = document.querySelector('.MuiBottomNavigation-root');
    const footer = document.querySelector('.footer');

    const updateHeights = () => {
      if (header) setHeaderHeight(header.offsetHeight);
      if (nav) setBottomNavHeight(nav.offsetHeight);
      if (footer) setFooterHeight(footer.offsetHeight);
    };

    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, []);

  const bottomOffset = isMobile ? bottomNavHeight : footerHeight;

  // 🔹 Charger les données du groupe, contacts et périodes
  useEffect(() => {
    if (!groupeId) return;
    
    axios
      .get(`${process.env.REACT_APP_API}/contacts?groupe=${groupeId}`)
      .then(res => setContacts(res.data))
      .catch(err => logDev("❌ Erreur chargement contacts", { message: err.message, stack: err.stack }));

    axios
      .get(`${process.env.REACT_APP_API}/titresperiodes/${groupeId}`)
      .then(res => setPeriodes(res.data))
      .catch(err => logDev("❌ Erreur chargement périodes", { message: err.message, stack: err.stack }));

    axios
      .get(`${process.env.REACT_APP_API}/groupes/${groupeId}`)
      .then(res => {      

        if (res.data.periodePrivilegiee !== periodePrivilegiee) {
          setPeriodePrivilegiee(res.data.periodePrivilegiee);
          }
        })
        .catch(err => logDev("❌ Erreur chargement groupe", { message: err.message, stack: err.stack }));
        }, [groupeId]);

          
      // 🔹 Charger les menus existants
          useEffect(() => {
            if (!groupeId) return;            

            axios
            .get(`${process.env.REACT_APP_API}/menusjournalier/${groupeId}`)
            .then(res => {
              logDev("📥 Menus reçus", res.data);
              setMenus(res.data);
              })
              .catch(err => logDev("❌ Erreur chargement menus", { message: err.message, stack: err.stack }));


            //}, [groupeId, periodePrivilegiee]);
          }, [groupeId]);


  // Egalité ou plusieurs dominantes
  if (periodeInfo && !periodeInfo.dominantKey && !periodePrivilegiee) {     

    return (

      <Paper sx={{       
        backgroundColor: "#f4ece2", 
        width: "90vw",
        margin: "0 auto", 
        padding: 2, 
      }}               
      >
             
        <Box sx={{ padding: 2, textAlign: "center" }}>

          <Typography color="error"> 
            ⚠️ {t("MultipleDominantPeriods")} — {t("AdminMustChooseOne")}
            </Typography>

        </Box>

        </Paper>
      );
  }

    if (!groupeId) return <Navigate to="/connexion" replace />;

  // 👉 Rendu principal

  return (
    <div className="menuscreation-container" 

    style={{
      minHeight: "100vh",
      paddingTop: `${topOffset}px`,
      paddingBottom: `${bottomOffset}px`,
      boxSizing: "border-box",
      backgroundColor: "#f4ece2",
      overflowY: "auto"
      }}
    >
      
      {isAdmin && groupeId && (
        <QuotaInfo groupeId={groupeId} isAdmin={isAdmin} />
        )}
        
        <Paper
        elevation={1}
        sx={{
          backgroundColor: "#f4ece2",
          width: "90vw",
          margin: "0 auto",
          padding: 2,
          }}
          >


            {/* 🔔 Notice rôle */}
            <div
            style={{                     
              backgroundColor: "#2f2f2fff",
              padding: "12px 16px",
              borderRadius: "6px",
              marginBottom: "12px",
              fontSize: "1rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "#fff",
              border: "2px solid #d4af37",
              boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
              backgroundImage:             
              "radial-gradient(circle at top left, rgba(255,255,255,0.05), transparent 70%)",
            }}
            >

              {isAdmin ? t("MenusAdmin") : t("MenusUsers")}


              </div>

              <Box sx={{ textAlign: "center", marginBottom: 2 }}>

                <Typography
                variant="h6"
                gutterBottom
                sx={{                
                  color: couleur,
                  fontWeight: "bold",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1
                }}
                >

                  <strong>{nomGroupeComplet}</strong>                

                  </Typography>

                  </Box>
                  {menus.length > 0 ? (
                    <>                 
                    <ListeMenusJournalier                 
                    menus={menus}
                    t={t}
                    groupeActif={groupeActif}
                    groupeId={groupeId}
                    isAdmin={isAdmin}
                    totalContactsPeriodeDominante={totalCombien}
                    />

                    <Box sx={{ mt: 6 }}>
          
                      <BlocPersonnalise              
                      customBlocks={customBlocks}
                      newBlockName={newBlockName}
                      setNewBlockName={setNewBlockName}
                      addCustomBlock={addCustomBlock}
                      handleCustomItemChange={handleCustomItemChange}
                      addCustomItem={addCustomItem}
                      removeCustomItem={removeCustomItem}
                      renameCustomBlock={renameCustomBlock}
                      />
                  </Box>
                </>
                ) : (
                  <Box                
                  sx={{
                    textAlign: "center",
                    padding: isMobile ? 2 : 4,
                    backgroundColor: "#e4dcd2",
                    borderRadius: 2,
                    marginBottom: 2,
                    color: "#555",
                    fontSize: isMobile ? "0.9rem" : "1rem",
                    fontStyle: "italic",
                    maxWidth: isMobile ? "90%" : "auto",
                    margin: "0 auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                💤 {isAdmin ? t("NoActiveDays") : t("NoActiveDaysUser")}
            </Box>
          )
        }
       

        {menus.length > 0 && (
          <Box sx={{ textAlign: "center", mt: 0.5 }}>

            <Button
              variant="contained"
              onClick={() => navigate("/liste")}
              sx={{
                backgroundColor: "#3399FF",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                paddingX: 3,
                paddingY: 1.2,
                '&:hover': {
                  backgroundColor: "#027bb5"
                }
              }}
            >
              🛒 {t("ViewShoppingList")}
        </Button>
      </Box>
    )}
      </Paper>
    </div>
  );
};

export default Menus;
